import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

// ============================================================
// Types
// ============================================================

export interface AuthUser {
  userId: string;
  email: string;
  displayName: string;
  preferredScoringMode?: 'simple' | 'intermediate' | 'advanced';
}

type AuthStatus = 'initializing' | 'authenticated' | 'unauthenticated';

interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Raw access token — stored in memory only, never persisted */
  accessToken: string | null;
  /** Called by AuthSuccessPage after OAuth redirect captures the token */
  setTokenFromRedirect: (token: string) => void;
  logout: () => Promise<void>;
  /** Manually trigger a refresh (used by api.ts on 401) */
  refresh: () => Promise<string | null>;
  /** Persist user preferences (fire-and-forget friendly) */
  updatePreferences: (prefs: { scoringMode?: 'simple' | 'intermediate' | 'advanced' }) => Promise<void>;
}

// ============================================================
// Context
// ============================================================

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

// ============================================================
// Provider
// ============================================================

/** How many ms before token expiry to proactively refresh (1 min before 15-min expiry) */
const REFRESH_AHEAD_MS = 60_000;
/** Access token TTL from server (15 min) */
const ACCESS_TOKEN_TTL_MS = 15 * 60 * 1000;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('initializing');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  // Ref for the proactive refresh timer so we can clear it
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Prevent concurrent refresh calls
  const refreshingRef = useRef<Promise<string | null> | null>(null);

  // ── Core refresh function ──────────────────────────────────

  const refresh = useCallback(async (): Promise<string | null> => {
    // If already refreshing, share the same promise
    if (refreshingRef.current) return refreshingRef.current;

    const doRefresh = async (): Promise<string | null> => {
      try {
        const res = await fetch('/api/auth/refresh', {
          method: 'POST',
          credentials: 'include', // sends the HttpOnly cookie
        });

        if (!res.ok) {
          setStatus('unauthenticated');
          setUser(null);
          setAccessToken(null);
          return null;
        }

        const data = (await res.json()) as { accessToken: string; expiresIn: number; user: { userId: string; email: string; displayName: string; preferences?: { scoringMode?: 'simple' | 'intermediate' | 'advanced' } } };
        setAccessToken(data.accessToken);
        setUser({
          userId: data.user.userId,
          email: data.user.email,
          displayName: data.user.displayName,
          preferredScoringMode: data.user.preferences?.scoringMode,
        });
        setStatus('authenticated');
        scheduleRefresh(data.expiresIn * 1000);
        return data.accessToken;
      } catch {
        setStatus('unauthenticated');
        setUser(null);
        setAccessToken(null);
        return null;
      } finally {
        refreshingRef.current = null;
      }
    };

    refreshingRef.current = doRefresh();
    return refreshingRef.current;
  }, []);

  // ── Schedule proactive token refresh ──────────────────────

  const scheduleRefresh = useCallback(
    (ttlMs: number) => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      const delay = Math.max(ttlMs - REFRESH_AHEAD_MS, 10_000);
      refreshTimerRef.current = setTimeout(() => {
        refresh();
      }, delay);
    },
    [refresh],
  );

  // ── On mount: attempt silent refresh ──────────────────────

  useEffect(() => {
    refresh().then((token) => {
      if (!token) setStatus('unauthenticated');
    });

    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, []);  // intentionally run once on mount

  // ── Refresh on tab/app focus (handles backgrounded PWA/browser tabs) ─────

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && status === 'authenticated') {
        // Tab just came back to foreground — refresh token immediately
        refresh();
      }
    };

    const handleFocus = () => {
      // Fallback for PWAs/mobile browsers that fire focus instead of visibilitychange
      if (status === 'authenticated') {
        refresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [status, refresh]);

  // ── Capture token from OAuth redirect ─────────────────────

  const setTokenFromRedirect = useCallback(
    (token: string) => {
      // Decode the payload (no signature verification needed here — server already verified)
      try {
        const payload = JSON.parse(atob(token.split('.')[1]!)) as AuthUser & { exp: number };
        setAccessToken(token);
        setUser({ userId: payload.userId, email: payload.email, displayName: payload.displayName });
        setStatus('authenticated');
        // Schedule refresh based on token expiry
        const ttlMs = payload.exp * 1000 - Date.now();
        scheduleRefresh(Math.max(ttlMs, ACCESS_TOKEN_TTL_MS));
      } catch {
        // Malformed token — fall back to a regular refresh attempt
        refresh();
      }
    },
    [refresh, scheduleRefresh],
  );

  // ── Update preferences ────────────────────────────────────

  const updatePreferences = useCallback(async (prefs: { scoringMode?: 'simple' | 'intermediate' | 'advanced' }) => {
    if (!accessToken) return;
    try {
      const res = await fetch('/api/users/me/preferences', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(prefs),
      });
      if (res.ok && prefs.scoringMode !== undefined) {
        setUser(prev => prev ? { ...prev, preferredScoringMode: prefs.scoringMode } : prev);
      }
    } catch {
      // Non-fatal — preference just won't persist
    }
  }, [accessToken]);

  // ── Logout ────────────────────────────────────────────────

  const logout = useCallback(async () => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    } catch {
      // Best-effort — clear local state regardless
    }
    setAccessToken(null);
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  // ── Context value ─────────────────────────────────────────

  const value: AuthContextValue = {
    status,
    user,
    isAuthenticated: status === 'authenticated',
    isLoading: status === 'initializing',
    accessToken,
    setTokenFromRedirect,
    logout,
    refresh,
    updatePreferences,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
